import React from "react";
import PropTypes from "prop-types";
import { ChevronDown, Check } from "lucide-react";
import "../Combobox/Combobox.css";
import "./MultiCombobox.css";

export default function MultiCombobox({
  id,
  values,
  options,
  onChange,
  label = "",
  placeholder = "Select...",
  className = "",
  style = {},
  buttonClass = "",
}) {
  const [open, setOpen] = React.useState(false);
  const [showAll, setShowAll] = React.useState(false);
  const ref = React.useRef();

  // Cierra el menú si se hace click fuera
  React.useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const valueSet = React.useMemo(() => new Set((values || []).map(String)), [values]);
  const selectedOptions = React.useMemo(
    () => options.filter((o) => valueSet.has(String(o.value))),
    [options, valueSet]
  );

  const summary = selectedOptions.length
    ? selectedOptions.map((o) => o.label).join(", ")
    : (label || placeholder);

  const listId = id ? `${id}-multicombobox` : "multicombobox-listbox";

  const MAX_VISIBLE = 5;
  const visibleOptions = React.useMemo(
    () => (showAll ? options : options.slice(0, MAX_VISIBLE)),
    [options, showAll]
  );

  const toggleValue = (val) => {
    const s = new Set((values || []).map(String));
    const sval = String(val);
    if (s.has(sval)) s.delete(sval);
    else s.add(sval);
    onChange(Array.from(s));
  };

  return (
    <div className={"combobox-root combobox-full " + className} style={style} ref={ref}>
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={label}
        aria-controls={listId}
        className={"combobox-btn " + buttonClass}
        onClick={() => setOpen((v) => !v)}
      >
        <span style={{ pointerEvents: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {summary}
        </span>
        <ChevronDown className="combobox-chevron" size={18} />
      </button>
      {open && (
        <ul className={`combobox-list ${showAll ? "combobox-list-expanded" : ""}`} role="listbox" id={listId}>
          {visibleOptions.map((opt) => {
            const isSelected = valueSet.has(String(opt.value));
            return (
              <li
                key={opt.value}
                role="option"
                aria-disabled={!!opt.disabled}
                aria-selected={isSelected}
                className={
                  "combobox-option" +
                  (isSelected ? " combobox-option-active" : "") +
                  (opt.disabled ? " combobox-option-disabled" : "")
                }
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault();
                  if (opt.disabled) return;
                  toggleValue(opt.value);
                }}
              >
                <div className="mcx-row">
                  <span className={"mcx-checkbox" + (isSelected ? " mcx-checkbox-on" : "")}>{isSelected && <Check size={14} />}</span>
                  <span>{opt.label}</span>
                </div>
              </li>
            );
          })}
          {options.length > MAX_VISIBLE && (
            <li
              className="combobox-option combobox-see-more"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowAll((prev) => !prev);
              }}
            >
              {showAll ? "See less" : "See more"}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

MultiCombobox.propTypes = {
  id: PropTypes.string,
  values: PropTypes.arrayOf(PropTypes.any).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  buttonClass: PropTypes.string,
};
